import { StaticImageData } from 'next/image';
import useSWR from 'swr';

import apiRequest from '@/services/apiRequest';
import { getSearchApi } from '@/utility/getCoreApi';
import getSeason from '@/utility/getSeason';

import { ILocationSearchAutocompleteListOption } from './useLocationSearchAutocompleteOptions';

interface IBreadcrumbsEntity {
  id: string;
  title: string;
  url?: string;
}

interface ImagesEntity {
  season: string;
  url: string;
}

const getImageUrlBySeason = (images: ImagesEntity[]) => {
  const date = new Date();
  const seasonByDate = getSeason(date);
  const img = images.find(({ season }) => season === seasonByDate);
  return img ? img.url : images[0]?.url;
};

type TSearchPopularParksResponse = {
  locale?: string | null;
  metadata?: any;
  id?: number | null;
}[];

type TPark = {
  id?: number | null;
  name: string;
  image?: string | StaticImageData;
  link: string;
  label?: string;
  sublabel?: string;
};

export const usePopularParks = <T = TPark[] | ILocationSearchAutocompleteListOption[]>(
  defaultLocations: T,
) => {
  const { data, error } = useSWR(
    `${getSearchApi()}/parks/popular`,
    (url: string) => apiRequest<TSearchPopularParksResponse>({ url }, true, true),
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const popularParksRes = data?.data || [];

  const shouldShowDefaultLocations = error || !popularParksRes.length;

  if (shouldShowDefaultLocations) {
    return defaultLocations;
  }

  const parsedResponse = popularParksRes.map(res => {
    if (!res) return null;
    const id = res.id;
    const { title, images, breadcrumbs, address } = res.metadata;

    const link = breadcrumbs.find(
      (breadcrumb: IBreadcrumbsEntity) => breadcrumb.id === String(id),
    )?.url;

    const image = getImageUrlBySeason(images);

    const hasStateName = address.state_name;
    const hasAddressWithState = address.state_code && title;

    return {
      id,
      name: title,
      image,
      link,
      label: title,
      sublabel: hasStateName ? address.state_name : undefined,
      value: hasAddressWithState ? `${title}, ${address.state_code}` : title,
    };
  });

  return parsedResponse;
};
